import { useMemo } from 'react';
import { format, differenceInDays } from 'date-fns';
import { Calendar, MapPin, Truck, Building2, Layers, Loader2 } from 'lucide-react';
import { useEnergyTimeSeries } from '@/hooks/useEnergyTimeSeries';
import type { CompareSlot, TimeGranularity } from '@/types/reports';
import type { Group, IndustryType } from '@/types';

interface CompareInfoSectionProps {
  slot: CompareSlot;
  dateRange: { start: Date | null; end: Date | null } | null;
  granularity: TimeGranularity;
  groups: Group[];
  selectedIndustryTypes: IndustryType[];
  /** Dynamic label for group (Production vs Group based on industry) */
  groupLabel?: string;
}

export function CompareInfoSection({
  slot,
  dateRange,
  granularity,
  groups,
  selectedIndustryTypes,
  groupLabel = 'Production',
}: CompareInfoSectionProps) {
  // Get the selected groups
  const selectedGroups = useMemo(() => {
    return slot.groupIds
      .map(id => groups.find(g => g.id === id))
      .filter((g): g is Group => !!g);
  }, [slot.groupIds, groups]);

  // Separate root groups from child groups (sites)
  const { rootGroups } = useMemo(() => {
    const selectedSet = new Set(slot.groupIds);
    const roots = selectedGroups.filter(
      g => !g.parent_group_id || !selectedSet.has(g.parent_group_id)
    );
    const children = selectedGroups.filter(
      g => g.parent_group_id && selectedSet.has(g.parent_group_id)
    );
    return { rootGroups: roots, childGroups: children };
  }, [selectedGroups, slot.groupIds]);

  // Get all child groups (sites) for each root group
  const sitesInfo = useMemo(() => {
    const result: { rootName: string; sites: string[] }[] = [];

    rootGroups.forEach(root => {
      // Find all children of this root that are in our selection
      const sites = groups
        .filter(g => g.parent_group_id === root.id && slot.groupIds.includes(g.id))
        .map(g => g.name);

      if (sites.length > 0) {
        result.push({ rootName: root.name, sites });
      }
    });

    return result;
  }, [rootGroups, groups, slot.groupIds]);

  // Helper to parse ISO date string as local date (avoids timezone offset issues)
  const parseLocalDate = (dateStr: string): Date => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // Get individual production date ranges for display
  const productionDateRanges = useMemo(() => {
    return rootGroups
      .filter(g => g.start_date || g.end_date)
      .map(g => ({
        name: g.name,
        start: g.start_date ? parseLocalDate(g.start_date) : null,
        end: g.end_date ? parseLocalDate(g.end_date) : null,
      }));
  }, [rootGroups]);

  // Get combined date range for backward compatibility (when only 1 production)
  const _groupDateRange = useMemo(() => {
    if (productionDateRanges.length === 0) return null;
    if (productionDateRanges.length === 1) {
      return {
        start: productionDateRanges[0].start,
        end: productionDateRanges[0].end,
      };
    }
    // Multiple productions - calculate combined range
    let earliestStart: Date | null = null;
    let latestEnd: Date | null = null;
    productionDateRanges.forEach(p => {
      if (p.start && (!earliestStart || p.start < earliestStart)) earliestStart = p.start;
      if (p.end && (!latestEnd || p.end > latestEnd)) latestEnd = p.end;
    });
    return earliestStart || latestEnd ? { start: earliestStart, end: latestEnd } : null;
  }, [productionDateRanges]);

  // Fetch time series data to get unit count
  const { timeSeries: timeSeriesData, isLoading } = useEnergyTimeSeries({
    groupIds: slot.groupIds,
    startDate: dateRange?.start || null,
    endDate: dateRange?.end || null,
    granularity,
    modelCodes: slot.modelCodes,
    industryTypes: selectedIndustryTypes,
    enabled: slot.groupIds.length > 0 && !!dateRange,
  });

  // Get max units active from time series
  const maxUnitsActive = useMemo(() => {
    if (!timeSeriesData || timeSeriesData.length === 0) return 0;
    return Math.max(...timeSeriesData.map(p => p.unitsActive || 0));
  }, [timeSeriesData]);

  if (slot.groupIds.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
        No groups selected
      </div>
    );
  }

  return (
    <div className="space-y-3 relative pb-3">
      {/* Units count - absolute positioned top right */}
      <div className="absolute top-0 right-0 flex items-center gap-1.5 text-[10px] text-muted-foreground uppercase tracking-wider">
        <Truck className="h-3.5 w-3.5" style={{ color: slot.color }} />
        {isLoading ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <span className="text-foreground font-medium">{maxUnitsActive}</span>
        )}
        <span>units</span>
      </div>

      {/* Productions - full width */}
      <div className="space-y-1 pr-20">
        <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground uppercase tracking-wider">
          <Building2 className="h-3.5 w-3.5" style={{ color: slot.color }} />
          {rootGroups.length === 1 ? 'Production' : 'Productions'}
        </div>
        <div className="text-sm font-medium">
          {rootGroups.length === 1 ? (
            rootGroups[0].name
          ) : (
            rootGroups.map((g, idx) => (
              <span key={g.id}>
                <span style={{ color: slot.color }}>{g.name}</span>
                {idx < rootGroups.length - 1 && <span className="text-muted-foreground">, </span>}
              </span>
            ))
          )}
        </div>
      </div>

      {/* Sites (child groups) - full width */}
      {sitesInfo.length > 0 && (
        <div className="space-y-1">
          <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground uppercase tracking-wider">
            <Layers className="h-3.5 w-3.5" style={{ color: slot.color }} />
            Sites
          </div>
          <div className="flex flex-wrap gap-1">
            {sitesInfo.flatMap((info, idx) =>
              info.sites.map((site, siteIdx) => (
                <span
                  key={`${idx}-${siteIdx}`}
                  className="px-2 py-0.5 bg-muted/50 rounded text-xs"
                >
                  {site}
                </span>
              ))
            )}
          </div>
        </div>
      )}

      {/* Analysis Period - full width, label above date */}
      {dateRange && (
        <div className="space-y-1">
          <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground uppercase tracking-wider">
            <Calendar className="h-3.5 w-3.5 shrink-0" style={{ color: slot.color }} />
            Analysis Period
          </div>
          <div className="text-xs whitespace-nowrap">
            {/* Show dynamic count when using production dates with multiple groups */}
            {slot.useFullGroup && rootGroups.length > 1 ? (
              <span className="text-foreground">{rootGroups.length} {groupLabel.toLowerCase()}s selected</span>
            ) : dateRange.start && dateRange.end ? (
              <>
                {format(dateRange.start, 'MMM d')}
                <span className="text-muted-foreground"> – </span>
                {format(dateRange.end, 'MMM d, yyyy')}
                <span className="text-muted-foreground ml-1">
                  ({differenceInDays(dateRange.end, dateRange.start) + 1}d)
                </span>
              </>
            ) : (
              <span className="text-muted-foreground">Not set</span>
            )}
          </div>
        </div>
      )}

      {/* Production Dates */}
      {productionDateRanges.length > 0 && (
        <div className="space-y-1">
          <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground uppercase tracking-wider">
            <MapPin className="h-3.5 w-3.5 shrink-0" style={{ color: slot.color }} />
            Production Dates
          </div>
          {productionDateRanges.length === 1 ? (
            <div className="text-xs whitespace-nowrap">
              {productionDateRanges[0].start && (
                <span>{format(productionDateRanges[0].start, 'MMM d')}</span>
              )}
              {productionDateRanges[0].start && productionDateRanges[0].end && (
                <span className="text-muted-foreground"> – </span>
              )}
              {productionDateRanges[0].end && (
                <span>{format(productionDateRanges[0].end, 'MMM d, yyyy')}</span>
              )}
              {productionDateRanges[0].start && productionDateRanges[0].end && (
                <span className="text-muted-foreground ml-1">
                  ({differenceInDays(productionDateRanges[0].end, productionDateRanges[0].start) + 1}d)
                </span>
              )}
              {!productionDateRanges[0].start && productionDateRanges[0].end && (
                <span>Ends {format(productionDateRanges[0].end, 'MMM d, yyyy')}</span>
              )}
              {productionDateRanges[0].start && !productionDateRanges[0].end && (
                <span>Ongoing</span>
              )}
            </div>
          ) : (
            <div className="space-y-0.5">
              {productionDateRanges.map((prod, idx) => (
                <div key={idx} className="text-xs whitespace-nowrap">
                  <span className="font-medium" style={{ color: slot.color }}>{prod.name}: </span>
                  {prod.start && (
                    <span>{format(prod.start, 'MMM d')}</span>
                  )}
                  {prod.start && prod.end && (
                    <span className="text-muted-foreground"> – </span>
                  )}
                  {prod.end && (
                    <span>{format(prod.end, 'MMM d, yyyy')}</span>
                  )}
                  {prod.start && prod.end && (
                    <span className="text-muted-foreground ml-1">
                      ({differenceInDays(prod.end, prod.start) + 1}d)
                    </span>
                  )}
                  {!prod.start && prod.end && (
                    <span>Ends {format(prod.end, 'MMM d, yyyy')}</span>
                  )}
                  {prod.start && !prod.end && (
                    <span>Ongoing</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
